'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope,
  ShieldCheck,
  Video,
  FileText,
  Wallet,
  ArrowRight,
  ArrowLeft,
  Calendar,
  Award,
  ChevronDown,
  Sparkles,
  HelpCircle,
  QrCode,
  Pill,
  CheckCircle2,
  Activity,
  Calculator,
  TrendingUp,
  DollarSign,
    Zap,
  BadgeCheck,
  Clock,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn } from '@/utils/cn';

type ShowcaseTab = 'memed' | 'express' | 'video' | 'wallet';

type IncomePreset = {
  id: string;
  label: string;
  weekly: number;
  price: number;
  badge: string;
  accent: string;
};

const INCOME_PRESETS: IncomePreset[] = [
  {
    id: 'leve',
    label: 'Ritmo Leve',
    badge: '8/sem · R$ 100',
    weekly: 8,
    price: 100,
    accent: 'from-sky-500 to-blue-600',
  },
  {
    id: 'parcial',
    label: 'Dedicação Parcial',
    badge: '18/sem · R$ 120',
    weekly: 18,
    price: 120,
    accent: 'from-blue-600 to-indigo-700',
  },
  {
    id: 'intensa',
    label: 'Alta Demanda',
    badge: '35/sem · R$ 150',
    weekly: 35,
    price: 150,
    accent: 'from-emerald-500 to-teal-700',
  },
];

export function DoctorLandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<ShowcaseTab>('memed');

  // Simulador Dinâmico de Ganhos Médicos
  const [consultasPorSemana, setConsultasPorSemana] = useState(15);
  const [valorPorConsulta, setValorPorConsulta] = useState(120);

  const consultasMensais = consultasPorSemana * 4;
  const faturamentoMensal = consultasMensais * valorPorConsulta;
  const faturamentoAnual = faturamentoMensal * 12;
  const horasSemanais = Math.round(consultasPorSemana * 0.5);
  const economiaConsultorioFisico = 3850;
  const ganhoLiquidoEstimado = Math.round(faturamentoMensal * 0.85);

  const applyPreset = (preset: IncomePreset) => {
    setConsultasPorSemana(preset.weekly);
    setValorPorConsulta(preset.price);
  };

  const faqs = [
    {
      question: 'A Medikei cobra mensalidade, anuidade ou taxa de adesão?',
      answer:
        'Não! O cadastro é 100% gratuito. Não cobramos mensalidades, anuidade de software nem taxas de adesão. A plataforma retém apenas a taxa operacional da consulta realizada, configurada de maneira transparente.',
    },
    {
      question: 'Como funciona a integração oficial com a Memed?',
      answer:
        'A Medikei possui integração de nível corporativo com a Memed. Diretamente na tela do atendimento, você acessa a base com mais de 60.000 medicamentos atualizados, avisos automáticos de interação medicamentosa e assina suas receitas (simples, controladas, antimicrobianos), atestados e pedidos de exames com certificado digital ICP-Brasil. O paciente recebe a receita autenticada no WhatsApp e SMS com QR Code aceito em todas as farmácias do país.',
    },
    {
      question: 'Preciso de certificado digital para prescrever?',
      answer:
        'Sim, para a emissão de receitas controladas e atestados digitais válidos em todo o território nacional é necessário possuir certificado digital padrão ICP-Brasil (tipo A1 ou em nuvem, como Bird ID, SafeID, Vidaas). A assinatura é realizada de forma transparente durante o encerramento do atendimento.',
    },
    {
      question: 'Como e quando recebo o repasse dos meus atendimentos?',
      answer:
        'Ao finalizar o atendimento, os honorários da consulta são processados automaticamente na sua Carteira Medikei. Você pode acompanhar o extrato detalhado por atendimento e solicitar transferências instantâneas via PIX para sua conta bancária a qualquer momento.',
    },
    {
      question: 'Posso atender pacientes de outros estados?',
      answer:
        'Sim! Conforme a Resolução CFM nº 2.314/2022, o médico com CRM regular e ativo em seu estado de origem tem autorização legal para realizar teleconsultas para pacientes localizados em qualquer estado do território brasileiro, sem necessidade de visto secundário para o formato à distância.',
    },
    {
      question: 'Como funciona a Fila Expressa (Plantão sob Demanda)?',
      answer:
        'Você decide seus próprios horários! Ao ativar o botão "Plantonista Online" no painel, seu perfil passa a receber convites de pacientes que entram na fila virtual de urgência. Cada solicitação traz a pré-triagem inteligente de sintomas feita pela nossa IA, e você tem tempo hábil para aceitar e iniciar a chamada em alta definição.',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground antialiased selection:bg-primary/20 selection:text-primary overflow-x-hidden">
      {/* ============================================================
          1. HEADER ESTILO DOCTORALIA (Vidro com contraste e respiro)
          ============================================================ */}
      <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-b border-slate-200/80 dark:border-white/10 transition-colors h-18 lg:h-22">
        <div className="container-landing h-full flex items-center justify-between gap-4 lg:gap-8">
          {/* Logo & Portal Badge */}
          <div className="flex items-center gap-3.5 shrink-0">
            <Link
              to="/"
              className="flex items-center gap-2 focus:outline-none group cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
            >
              <img
                src="/logo.png"
                alt="Medikei"
                className="h-8 sm:h-9 lg:h-10 w-auto dark:brightness-110 drop-shadow-xs"
              />
              <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/70 dark:border-blue-800/60 shadow-2xs">
                <Stethoscope className="w-3.5 h-3.5 text-blue-600" />
                <span>Portal do Médico</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1 bg-transparent px-1 py-1">
            <a
              href="#memed"
              className="px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100/80 dark:hover:bg-white/10 transition-all duration-200"
            >
              Prescrição Memed
            </a>
            <a
              href="#simulador"
              className="px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100/80 dark:hover:bg-white/10 transition-all duration-200 flex items-center gap-1.5"
            >
              <Calculator className="w-3.5 h-3.5 text-blue-600" />
              <span>Simulador de Renda</span>
            </a>
            <a
              href="#como-funciona"
              className="px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100/80 dark:hover:bg-white/10 transition-all duration-200"
            >
              Como Funciona
            </a>
            <a
              href="#recursos"
              className="px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100/80 dark:hover:bg-white/10 transition-all duration-200"
            >
              Recursos
            </a>
          </nav>

          {/* User Actions - Generously Spaced */}
          <div className="flex items-center gap-2.5 sm:gap-3.5 shrink-0">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full hover:bg-muted/60 border border-border/40"
              title="Ir para a página inicial de pacientes"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Área do Paciente</span>
            </Link>

            <ThemeToggle />

            <div className="hidden sm:block h-5 w-px bg-border/80 dark:bg-white/10" />

            <Link
              to="/login"
              className="text-xs sm:text-sm font-semibold text-foreground/80 hover:text-primary transition-colors px-3 py-1.5 rounded-full hover:bg-muted/50"
            >
              Entrar
            </Link>

            <Link to="/register/doctor">
              <Button className="rounded-full font-bold text-xs sm:text-sm h-10 px-5 shadow-md shadow-primary/25 gap-2 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white">
                <span>Criar Conta Médica</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ============================================================
          2. HERO IMERSIVO EM AZUL REAL PROFUNDO (Doctoralia + AI Studio)
          ============================================================ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-900 via-blue-800 to-blue-950 text-white pt-12 pb-24 sm:pt-16 sm:pb-32">
        {/* Glows ambientes */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full bg-blue-400 blur-3xl opacity-25 animate-glow-ambient" />
          <div
            className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-cyan-400 blur-3xl opacity-20 animate-glow-ambient"
            style={{ animationDelay: '2s' }}
          />
          <div
            className="absolute -bottom-32 left-1/3 w-[380px] h-[380px] rounded-full bg-indigo-500 blur-3xl opacity-25 animate-glow-ambient"
            style={{ animationDelay: '4s' }}
          />
          {/* Padrão de grade sutil */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_35%,#000_70%,transparent_100%)] pointer-events-none" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          {/* Badge Superior Flutuante */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-950/80 backdrop-blur-md border border-blue-400/40 text-xs sm:text-sm font-semibold text-blue-100 shadow-lg animate-float-gentle">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            <span>Ecossistema Oficial · Memed &amp; ICP-Brasil</span>
          </div>

          {/* Headline Gigante */}
          <h1 className="mt-6 text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight font-display text-left">
            Atenda pacientes de <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-200 to-white">todo o Brasil</span>
            <br className="hidden sm:block" />
            com liberdade, tecnologia e repasse via <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-blue-100 to-emerald-200">PIX</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-blue-100/90 max-w-3xl leading-relaxed font-normal text-left">
            Prescreva com a <strong className="text-white">Memed oficial</strong>, atenda plantões sob demanda na fila expressa ou gerencie sua agenda particular. Sem mensalidades, sem custos de setup e com segurança jurídica garantida pelo CFM.
          </p>

          {/* CTAs Principais */}
          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-3 sm:gap-4 max-w-xl">
            <Link to="/register/doctor" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto rounded-xl font-bold h-12 px-8 text-base shadow-xl shadow-blue-500/35 bg-white text-blue-950 hover:bg-blue-50 hover:shadow-2xl hover:-translate-y-0.5 transition-all gap-2 cursor-pointer">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span>Cadastrar-se Gratuitamente</span>
              </Button>
            </Link>
            <a href="#como-funciona" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto rounded-xl font-semibold h-12 px-6 text-sm bg-transparent text-white border-white/30 hover:bg-white/10 hover:border-white/60 hover:text-white transition-all cursor-pointer"
              >
                <Activity className="w-4 h-4 mr-1.5" />
                Como Funciona na Prática
              </Button>
            </a>
          </div>

          {/* Estatísticas de Confiança */}
          <div className="mt-14 max-w-5xl grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 pt-8 border-t border-white/15 text-left">
            {[
              {
                icon: ShieldCheck,
                value: '30.000+',
                label: 'Pacientes ativos',
                color: 'text-emerald-300',
              },
              {
                icon: FileText,
                value: '60.000+',
                label: 'Medicamentos Memed',
                color: 'text-cyan-300',
              },
              {
                icon: BadgeCheck,
                value: 'CFM',
                label: 'Homologado & Conforme',
                color: 'text-amber-300',
              },
              {
                icon: Zap,
                value: '< 6 min',
                label: 'Tempo médio no Plantão',
                color: 'text-white',
              },
            ].map(({ icon: Icon, value, label, color }) => (
              <div
                key={label}
                className="p-3 rounded-2xl transition-all duration-200 hover:bg-white/5 cursor-default text-left"
              >
                <Icon className={cn('w-6 h-6 mb-2', color)} />
                <div className="text-xl sm:text-2xl font-black text-white tabular-nums">{value}</div>
                <div className="text-[11px] sm:text-xs text-blue-200 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          3. BANNER SOBREPOSTO: SUPORTE MEMED + ICP-BRASIL + RECEITA DIGITAL
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-12 relative z-20">
        <div className="bg-gradient-to-r from-blue-950 via-indigo-950 to-slate-950 rounded-3xl p-6 sm:p-8 text-white shadow-2xl border border-blue-500/30 overflow-hidden relative group">
          <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none animate-glow-ambient" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 text-xs font-bold uppercase tracking-wider shadow-sm">
                  <Award className="w-3.5 h-3.5" />
                  Receituário Inteligente
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-wider shadow-sm">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  ICP-Brasil Certificado
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
                Prescreva em Segundos, com Validade Jurídica Nacional.
              </h2>
              <p className="text-sm text-blue-100/90 leading-relaxed">
                Integração oficial com a Memed: catálogo com +60.000 medicamentos, alertas automáticos de interação medicamentosa e envio instantâneo por WhatsApp/SMS com QR Code válido em qualquer farmácia do país.
              </p>
              <div className="flex flex-wrap gap-4 text-xs text-blue-200 pt-1">
                {[
                  { icon: Pill, text: '+60.000 medicamentos' },
                  { icon: ShieldCheck, text: 'Alertas de interação automática' },
                  { icon: QrCode, text: 'QR Code em 100% das farmácias' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-cyan-300" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col items-start gap-4 bg-slate-900/90 backdrop-blur-md rounded-2xl p-5 border border-white/20 shrink-0 shadow-xl transition-all duration-300 hover:border-emerald-400/50">
              <div className="text-left">
                <div className="text-xs text-blue-200 uppercase font-semibold">Plano Memed</div>
                <div className="text-3xl font-black text-white font-display">
                  Incluso
                </div>
                <div className="text-[11px] text-emerald-300 font-medium">Sem custo de assinatura adicional</div>
              </div>
              <Link to="/register/doctor" className="w-full sm:w-auto lg:w-full">
                <button
                  type="button"
                  className="w-full px-6 py-3.5 bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 hover:from-cyan-300 hover:to-blue-300 text-slate-950 font-black text-sm rounded-xl shadow-xl shadow-cyan-500/40 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer whitespace-nowrap"
                >
                  <FileText className="w-4 h-4 text-slate-950" />
                  <span>Experimentar Agora</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          4. CALCULADORA INTERATIVA DE HONORÁRIOS (Sliders Premium)
          ============================================================ */}
      <section id="simulador" className="py-16 sm:py-24 bg-gradient-to-b from-background to-blue-950/5 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-blue-500/5 blur-3xl rounded-full pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-left max-w-3xl mb-12 space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800/60 text-blue-700 dark:text-blue-300 text-xs font-extrabold uppercase tracking-wider shadow-2xs">
              <Calculator className="w-3.5 h-3.5" />
              <span>Calculadora de Honorários</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground tracking-tight text-left">
              Quanto você pode faturar na Medikei?
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed text-left">
              Ajuste o volume de atendimentos e o valor da sua consulta para simular sua receita mensal sem custos fixos de estrutura.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Card de Sliders (7 cols) */}
            <div className="lg:col-span-7 p-6 sm:p-8 rounded-3xl bg-card border-2 border-border/80 dark:border-white/10 shadow-2xl space-y-7">
              {/* Presets Rápidos */}
              <div>
                <label className="block text-xs font-extrabold uppercase tracking-wider text-muted-foreground mb-3">
                  Cenários Recomendados
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {INCOME_PRESETS.map((preset) => {
                    const isActive =
                      consultasPorSemana === preset.weekly && valorPorConsulta === preset.price;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        className={cn(
                          'px-3 py-3 rounded-2xl text-left text-xs font-bold transition-all cursor-pointer border-2',
                          isActive
                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 shadow-sm'
                            : 'border-border bg-card text-muted-foreground hover:text-foreground hover:border-blue-400 hover:bg-blue-50/40 dark:hover:bg-blue-950/30'
                        )}
                      >
                        <span className="block text-[11px] uppercase tracking-wider opacity-80">
                          {preset.label}
                        </span>
                        <span className="block text-sm font-black mt-0.5">{preset.badge}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Slider 1: Consultas */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>Consultas por Semana</span>
                  </label>
                  <span className="text-base font-black text-primary px-3 py-1 bg-blue-50 dark:bg-blue-950/50 rounded-xl border border-blue-200/70 dark:border-blue-800/60">
                    {consultasPorSemana} cons./sem
                  </span>
                </div>
                <input
                  type="range"
                  min={4}
                  max={60}
                  step={1}
                  value={consultasPorSemana}
                  onChange={(e) => setConsultasPorSemana(parseInt(e.target.value, 10))}
                  className="w-full h-2.5 bg-blue-100 dark:bg-blue-950/50 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-[11px] text-muted-foreground font-semibold">
                  <span>4 (Pontual)</span>
                  <span className="text-blue-600 dark:text-blue-400 font-extrabold">~{horasSemanais}h/semana</span>
                  <span>60 (Intensivo)</span>
                </div>
              </div>

              {/* Slider 2: Valor */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-foreground flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    <span>Valor por Consulta</span>
                  </label>
                  <span className="text-base font-black text-emerald-700 dark:text-emerald-400 px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-200/70 dark:border-emerald-800/60">
                    R$ {valorPorConsulta},00
                  </span>
                </div>
                <input
                  type="range"
                  min={60}
                  max={350}
                  step={10}
                  value={valorPorConsulta}
                  onChange={(e) => setValorPorConsulta(parseInt(e.target.value, 10))}
                  className="w-full h-2.5 bg-emerald-100 dark:bg-emerald-950/50 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
                <div className="flex justify-between text-[11px] text-muted-foreground font-semibold">
                  <span>R$ 60 (Acessível)</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-extrabold">Média: R$ 120</span>
                  <span>R$ 350 (Especialista)</span>
                </div>
              </div>

              {/* Destaque de Economia */}
              <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center gap-3 text-xs sm:text-sm text-emerald-900 dark:text-emerald-200">
                <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
                <span>
                  <strong>Economia de ~R$ {economiaConsultorioFisico.toLocaleString('pt-BR')}/mês</strong> em aluguel, condomínio, secretária física e custos de infraestrutura presencial.
                </span>
              </div>
            </div>

            {/* Card de Resultado (5 cols) */}
            <div className="lg:col-span-5 p-7 sm:p-8 rounded-3xl bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950 text-white shadow-2xl border border-white/20 space-y-6 flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-blue-200 flex items-center gap-1.5 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-300" />
                  Estimativa de Faturamento
                </span>
                <p className="text-xs text-blue-200/80">
                  Baseado em <strong className="text-white">{consultasMensais}</strong> consultas mensais ({consultasPorSemana}/semana):
                </p>

                <div className="mt-5 pt-5 border-t border-white/15">
                  <span className="text-xs text-blue-200 block font-semibold">Faturamento Bruto Mensal Estimado</span>
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mt-1">
                    R$ {faturamentoMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-emerald-300 font-semibold mt-2 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Ganho líquido aproximado:{' '}
                    <strong>
                      R$ {ganhoLiquidoEstimado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês
                    </strong>
                  </p>
                </div>

                <div className="mt-5 p-3.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 text-xs space-y-1.5">
                  <div className="flex justify-between text-blue-100">
                    <span>Projeção Anual:</span>
                    <strong className="text-white">
                      R$ {faturamentoAnual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <div className="flex justify-between text-blue-100">
                    <span>Dedicação semanal:</span>
                    <strong className="text-white">~{horasSemanais} horas/semana</strong>
                  </div>
                  <div className="flex justify-between text-blue-100">
                    <span>Repasse dos honorários:</span>
                    <strong className="text-emerald-300">Instantâneo via PIX</strong>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Link to="/register/doctor" className="w-full block">
                  <Button
                    size="lg"
                    className="w-full rounded-2xl font-black text-sm sm:text-base h-12 text-slate-950 bg-emerald-400 hover:bg-emerald-300 shadow-xl shadow-emerald-500/30 transition-all hover:scale-[1.02] active:scale-98 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>Começar a Atender Agora</span>
                    <ArrowRight className="w-4 h-4 text-slate-950" />
                  </Button>
                </Link>
                <p className="text-[11px] text-blue-200 text-left mt-2">
                  Cadastro 100% gratuito · Sem mensalidades ou fidelidade
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          5. SHOWCASE INTERATIVO DO CONSULTÓRIO (4 Abas)
          ============================================================ */}
      <section id="como-funciona" className="py-16 sm:py-24 bg-muted/30 border-y border-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-3xl mb-12 space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
              Experiência Prática
            </h2>
            <p className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground tracking-tight">
              Explore o painel médico por dentro
            </p>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Interface desenhada para que você gaste tempo cuidando de pessoas, não preenchendo formulários lentos.
            </p>
          </div>

          <div className="flex items-center justify-start gap-2 mb-8 overflow-x-auto pb-2 scrollbar-none sm:flex-wrap">
            {(
              [
                { id: 'memed', label: 'Prescrição Memed', icon: FileText, color: 'text-blue-600' },
                { id: 'express', label: 'Plantão Express', icon: Zap, color: 'text-amber-500' },
                { id: 'video', label: 'Telemedicina HD', icon: Video, color: 'text-cyan-600' },
                { id: 'wallet', label: 'Carteira & Saques PIX', icon: Wallet, color: 'text-emerald-600' },
              ] as const
            ).map(({ id, label, icon: Icon, color }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id as ShowcaseTab)}
                className={cn(
                  'px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer flex items-center gap-2 border whitespace-nowrap shrink-0',
                  activeTab === id
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-600/30 border-blue-600 scale-[1.02]'
                    : 'bg-card text-muted-foreground hover:text-foreground border-border hover:border-primary/40'
                )}
              >
                <Icon className={cn('w-4 h-4', activeTab === id ? 'text-white' : color)} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="max-w-5xl p-6 sm:p-8 rounded-3xl border border-border bg-card shadow-xl min-h-[300px] flex items-center justify-start text-left">
            {activeTab === 'memed' && (
              <div className="w-full space-y-4 animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                    <Pill className="w-4 h-4 text-primary" />
                    <span>Emissão de Prescrição com Autenticação ICP-Brasil</span>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                    Memed Ativa
                  </span>
                </div>
                <div className="p-4 rounded-xl bg-muted/40 border border-border space-y-2 text-xs">
                  <p className="font-semibold text-foreground">Item 1: Amoxicilina + Clavulanato de Potássio 875mg/125mg</p>
                  <p className="text-muted-foreground leading-relaxed">
                    Tomar 1 comprimido por via oral de 12 em 12 horas durante 7 dias. Não suspender o tratamento antes do prazo.
                  </p>
                  <div className="flex items-center gap-3 pt-2 text-[11px] text-muted-foreground border-t border-border/60">
                    <span>✓ Sem contraindicação alérgica</span>
                    <span>✓ Assinatura digital ICP-Brasil</span>
                    <span>✓ Envio WhatsApp automático</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'express' && (
              <div className="w-full space-y-4 animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span>Chamada Recebida no Plantão Express</span>
                  </div>
                  <span className="text-xs font-bold text-rose-600 bg-rose-500/10 px-2.5 py-0.5 rounded-full animate-pulse">
                    Expira em 42s
                  </span>
                </div>
                <div className="p-4 rounded-xl bg-muted/40 border border-border space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <p className="font-bold text-sm text-foreground">Paciente: Maria Eduarda Siqueira (34 anos)</p>
                    <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold">
                      Prioridade Amarela
                    </span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>Triagem IA:</strong> Sintomas de dor de cabeça súbita há 2 dias, sem febre, pressão arterial controlada. Solicita atendimento imediato.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'video' && (
              <div className="w-full space-y-4 animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                    <Video className="w-4 h-4 text-primary" />
                    <span>Sala Segura de Teleconsulta HD</span>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-500/10 px-2.5 py-0.5 rounded-full">
                    Conexão Criptografada
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-4 rounded-xl bg-muted/50 border border-border space-y-2">
                    <p className="font-bold text-foreground">Vídeo em Alta Definição</p>
                    <p className="text-muted-foreground">Funciona sem instalar aplicativos adicionais, compatível com Chrome, Safari, Edge e Firefox em computadores e celulares.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 border border-border space-y-2">
                    <p className="font-bold text-foreground">Prontuário Lado a Lado</p>
                    <p className="text-muted-foreground">Visualize o histórico clínico, alergias e exames do paciente enquanto conversa por vídeo na mesma tela.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'wallet' && (
              <div className="w-full space-y-4 animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                    <Wallet className="w-4 h-4 text-emerald-600" />
                    <span>Carteira Profissional &amp; Repasses Instantâneos</span>
                  </div>
                  <span className="text-xs font-semibold text-foreground bg-muted px-2.5 py-0.5 rounded-full font-mono">
                    PIX Ativo
                  </span>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-xs flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Saldo Disponível para Transferência:</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Repasses automáticos calculados de forma discriminada por consulta.
                    </p>
                  </div>
                  <Button size="sm" className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm">
                    Transferir via PIX
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============================================================
          6. FLUXO OPERACIONAL DE PONTA A PONTA (4 Passos)
          ============================================================ */}
      <section className="py-16 sm:py-24 bg-background relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-left max-w-3xl mb-12 space-y-2">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/70 dark:border-blue-800/60 shadow-2xs">
              <Activity className="w-3.5 h-3.5" />
              <span>Fluxo Operacional de Ponta a Ponta</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground tracking-tight text-left">
              Como a Medikei funciona na sua rotina clínica
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed text-left">
              Entenda como cada módulo da plataforma se conecta desde a chegada do paciente até o fechamento do atendimento e repasse financeiro.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                n: '01',
                title: 'Entrada & Triagem IA',
                desc: 'O paciente entra na fila de urgência ou agenda com você. Nossa inteligência clínica realiza a pré-triagem dos sintomas e classifica o risco para apoiar sua decisão.',
                tag: 'Anamnese prévia pronta',
                color: 'bg-blue-500/10 text-blue-600',
              },
              {
                n: '02',
                title: 'Chamada & Teleconsulta HD',
                desc: 'Você recebe um alerta sonoro e visual em tempo real no seu painel. Aceite o atendimento com um clique e inicie a chamada em alta definição diretamente no navegador.',
                tag: 'Sem baixar aplicativos',
                color: 'bg-emerald-500/10 text-emerald-600',
              },
              {
                n: '03',
                title: 'Prontuário & Memed',
                desc: 'Acesse o histórico e exames do paciente lado a lado com a tela de vídeo. Prescreva medicamentos, atestados e exames com assinatura digital ICP-Brasil em segundos.',
                tag: '+60.000 itens da Memed',
                color: 'bg-purple-500/10 text-purple-600',
              },
              {
                n: '04',
                title: 'Envio & Repasse PIX',
                desc: 'O paciente recebe a receita e atestado no WhatsApp com QR Code válido em farmácias, e os honorários são creditados na sua Carteira digital com saque rápido via PIX.',
                tag: 'Saldo e extrato em tempo real',
                color: 'bg-amber-500/10 text-amber-600',
              },
            ].map(({ n, title, desc, tag, color }) => (
              <div
                key={n}
                className="p-6 sm:p-7 rounded-3xl border border-border bg-card shadow-2xs space-y-4 relative flex flex-col justify-between hover:border-blue-500 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300"
              >
                <div>
                  <div
                    className={cn(
                      'w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg mb-4',
                      color,
                    )}
                  >
                    {n}
                  </div>
                  <h3 className="font-bold text-base text-foreground mb-2">{title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
                <div className="pt-3 border-t border-border/60 text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>{tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          7. COMPARATIVO CONSULTÓRIO FÍSICO vs MEDIKEI DIGITAL
          ============================================================ */}
      <section className="py-16 sm:py-24 bg-muted/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-3xl mb-12 space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
              Comparativo Real de Modelo Operacional
            </h2>
            <p className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight text-left">
              Consultório Tradicional vs. Medikei Digital
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed text-left">
              Veja as vantagens práticas de operar seu consultório digital na Medikei
            </p>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <div className="min-w-[560px] sm:min-w-full rounded-3xl border border-border bg-card shadow-xl overflow-hidden">
              <div className="grid grid-cols-12 bg-muted/60 p-4 sm:p-5 border-b border-border text-xs sm:text-sm font-bold text-foreground">
                <div className="col-span-4 text-left">Aspecto Operacional</div>
                <div className="col-span-4 text-left text-muted-foreground">Consultório Físico</div>
                <div className="col-span-4 text-left text-primary">Plataforma Medikei</div>
              </div>

              <div className="divide-y divide-border/60 text-xs sm:text-sm">
                {[
                  {
                    label: 'Custos de Estrutura',
                    old: 'Aluguel, condomínio, IPTU e reformas',
                    new: '100% em Nuvem (Zero Custo Fixo)',
                    highlight: 'emerald',
                  },
                  {
                    label: 'Prescrição Oficial Memed',
                    old: 'Contratação de licença separada',
                    new: 'Inclusa com ICP-Brasil',
                    highlight: 'primary',
                  },
                  {
                    label: 'Alcance Geográfico',
                    old: 'Restrito ao raio local do imóvel',
                    new: 'Pacientes de todo o Brasil',
                    highlight: 'foreground',
                  },
                  {
                    label: 'Gestão de Agenda',
                    old: 'Dependência de secretária e horários fixos',
                    new: 'Fila sob demanda e agendamento híbrido',
                    highlight: 'emerald',
                  },
                  {
                    label: 'Cobrança e Honorários',
                    old: 'Risco de inadimplência e taxas de maquininha',
                    new: 'Pagamento garantido e saque PIX',
                    highlight: 'primary',
                  },
                ].map(({ label, old, new: newText, highlight }) => (
                  <div key={label} className="grid grid-cols-12 p-4 sm:p-5 items-center">
                    <div className="col-span-4 font-semibold text-foreground text-left">{label}</div>
                    <div className="col-span-4 text-muted-foreground text-left">{old}</div>
                    <div
                      className={cn(
                        'col-span-4 font-bold text-left',
                        highlight === 'emerald' && 'text-emerald-600 dark:text-emerald-400',
                        highlight === 'primary' && 'text-primary',
                        highlight === 'foreground' && 'text-foreground',
                      )}
                    >
                      {newText}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          8. GRID DE RECURSOS ESPECIALIZADOS
          ============================================================ */}
      <section id="recursos" className="py-16 sm:py-24 bg-muted/30 border-y border-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-3xl mb-12 space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
              Recursos Pensados para a Sua Rotina
            </h2>
            <p className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground tracking-tight text-left">
              Tudo o que você precisa para atender bem em um só lugar
            </p>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed text-left">
              Desenvolvemos cada ferramenta pensando na facilidade de uso, segurança jurídica e eficiência para o profissional de saúde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: 'Plantão Express 24/7',
                badge: 'Atendimento sob Demanda',
                description:
                  'Fique online quando quiser com um clique. Receba notificações de chamadas em tempo real com triagem prévia inteligente de sintomas realizada por IA.',
                gradient: 'from-amber-500/20 to-orange-500/10',
              },
              {
                icon: FileText,
                title: 'Receituário Digital Oficial Memed',
                badge: 'Padrão ICP-Brasil',
                description:
                  'Catálogo atualizado com mais de 60.000 medicamentos, assinatura digital em conformidade com o ITI e envio automático via WhatsApp ao paciente.',
                gradient: 'from-blue-500/20 to-indigo-500/10',
              },
              {
                icon: Video,
                title: 'Telemedicina HD no Navegador',
                badge: 'Sem Apps para Instalar',
                description:
                  'Salas de teleconsulta com áudio e vídeo de alta definição, criptografia de ponta a ponta e chat integrado. Funciona direto no navegador do médico e do paciente.',
                gradient: 'from-emerald-500/20 to-teal-500/10',
              },
              {
                icon: Wallet,
                title: 'Carteira & Repasses Transparentes',
                badge: 'Receba via PIX',
                description:
                  'Acompanhe seus honorários de forma discriminada por consulta. Solicite saques diretos para sua chave PIX com total clareza e sem mensalidade de plataforma.',
                gradient: 'from-emerald-500/20 to-cyan-500/10',
              },
              {
                icon: Calendar,
                title: 'Agenda Inteligente Híbrida',
                badge: 'Vídeo e Domiciliar',
                description:
                  'Gerencie seus horários disponíveis para teleconsultas ou consultas domiciliares (Home Care) com definição de raio de atuação e cálculo automático de deslocamento.',
                gradient: 'from-purple-500/20 to-pink-500/10',
              },
              {
                icon: ShieldCheck,
                title: 'Segurança Jurídica & PEP',
                badge: 'Resolução CFM 2.314/2022',
                description:
                  'Prontuário eletrônico unificado com linha do tempo clínica, histórico de atendimentos e guarda em nuvem em total conformidade com a LGPD e o CFM.',
                gradient: 'from-blue-500/20 to-sky-500/10',
              },
            ].map(({ icon: Icon, title, badge, description, gradient }) => (
              <div
                key={title}
                className="relative p-6 sm:p-7 rounded-3xl border border-border/80 bg-card hover:border-blue-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group overflow-hidden"
              >
                <div
                  className={`absolute -right-8 -top-8 w-28 h-28 bg-gradient-to-br ${gradient} rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500 pointer-events-none opacity-60`}
                />

                <div>
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 flex items-center justify-center group-hover:scale-110 group-hover:bg-gradient-to-br group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:text-white transition-all duration-300 shadow-xs">
                      <Icon className="w-6 h-6" />
                    </div>
                    {badge && (
                      <span className="text-[11px] font-extrabold px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/70 dark:border-blue-800/60 shadow-xs tracking-wide">
                        {badge}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2.5 group-hover:text-primary transition-colors">
                    {title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          9. COMO FUNCIONA O CREDENCIAMENTO (4 Passos)
          ============================================================ */}
      <section className="py-16 sm:py-20 bg-muted/20 border-t border-border/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-3xl mb-12 space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
              Processo de Entrada
            </h2>
            <p className="text-3xl font-extrabold text-foreground tracking-tight text-left">
              Como funciona o credenciamento médico
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed text-left">
              Um fluxo rigoroso que valoriza os bons profissionais e garante a confiança dos pacientes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            {[
              {
                n: '1',
                title: 'Cadastro e CRM',
                desc: 'Preencha seus dados básicos e informe seu CRM e UF para conferência imediata junto à base oficial do CFM.',
              },
              {
                n: '2',
                title: 'Envio dos Documentos',
                desc: 'Anexe de forma simples a carteira do CRM, o diploma de medicina e documento oficial de identidade com foto.',
              },
              {
                n: '3',
                title: 'Auditoria Médica',
                desc: 'Nossa equipe administrativa e técnica confere a regularidade dos documentos enviados com máxima agilidade.',
              },
              {
                n: '4',
                title: 'Conta Ativada',
                desc: 'Com o cadastro homologado, seu perfil é liberado para atendimentos, plantão express e emissão de receitas Memed.',
                highlight: true,
              },
            ].map(({ n, title, desc, highlight }) => (
              <div
                key={n}
                className={cn(
                  'p-6 rounded-3xl border space-y-3 hover:border-primary/40 transition-colors',
                  highlight
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/40 shadow-md'
                    : 'border-border bg-card shadow-2xs',
                )}
              >
                <span
                  className={cn(
                    'w-9 h-9 rounded-xl font-black text-sm flex items-center justify-center shadow-xs',
                    highlight
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/70 dark:border-blue-800/60',
                  )}
                >
                  {n}
                </span>
                <h4
                  className={cn(
                    'font-bold text-base',
                    highlight ? 'text-blue-700 dark:text-blue-300' : 'text-foreground',
                  )}
                >
                  {title}
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-left">
            <Link to="/register/doctor">
              <Button className="w-full sm:w-auto rounded-xl font-bold h-11 px-8 shadow-md shadow-primary/20 gap-2 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <span>Iniciar Meu Cadastro Agora</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          10. PERGUNTAS FREQUENTES (FAQ)
          ============================================================ */}
      <section className="py-16 sm:py-20 bg-muted/20 border-t border-border/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12 space-y-2">
            <div className="inline-flex items-center gap-2 p-2 rounded-2xl bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300">
              <HelpCircle className="w-5 h-5" />
            </div>
            <h2 className="text-3xl font-extrabold text-foreground text-left">Dúvidas Frequentes</h2>
            <p className="text-sm text-muted-foreground leading-relaxed text-left">
              Respostas claras para as principais perguntas dos médicos
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={faq.question}
                  className="rounded-2xl border border-border bg-card overflow-hidden transition-all shadow-2xs hover:border-blue-300/70"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-foreground text-sm sm:text-base hover:text-primary transition-colors cursor-pointer"
                  >
                    <span>{faq.question}</span>
                    <ChevronDown
                      className={cn(
                        'w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200',
                        isOpen && 'rotate-180 text-primary',
                      )}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 text-xs sm:text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-3 animate-in fade-in duration-200">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          11. CTA FINAL VIBRANTE AZUL PROFUNDO
          ============================================================ */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white">
        {/* Glows Ambientais */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full bg-blue-400 blur-3xl opacity-25 animate-glow-ambient" />
          <div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-indigo-400 blur-3xl opacity-20 animate-glow-ambient"
            style={{ animationDelay: '2s' }}
          />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-left relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-xs font-bold uppercase tracking-wider shadow-lg">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span>Pronto para começar?</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight text-left">
            Transforme sua prática médica digital com a Medikei.
          </h2>
          <p className="text-base sm:text-lg opacity-95 max-w-2xl leading-relaxed text-left">
            Cadastre-se gratuitamente hoje, ative seu plantão e prescreva com a Memed para pacientes de todo o Brasil com total respaldo ético e legal.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-3">
            <Link to="/register/doctor" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto rounded-2xl font-black text-base h-14 px-10 shadow-2xl bg-white text-blue-950 hover:bg-blue-50 hover:shadow-cyan-500/40 hover:-translate-y-0.5 transition-all cursor-pointer gap-2"
              >
                <Sparkles className="w-5 h-5 text-blue-600" />
                <span>Criar Minha Conta Médica</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto rounded-2xl font-bold text-base h-14 px-8 bg-transparent text-white border-white/30 hover:bg-white/10 hover:border-white transition-all cursor-pointer"
              >
                Já tenho conta
              </Button>
            </Link>
          </div>
          <div className="pt-4 flex flex-wrap items-center justify-start gap-4 sm:gap-6 text-xs text-blue-100/90">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-300" />
              <span>Sem mensalidade</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-cyan-300" />
              <span>Aprovação em até 24h</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-blue-300" />
              <span>Pacientes de todo o Brasil</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          12. FOOTER
          ============================================================ */}
      <footer className="py-12 border-t border-border bg-card text-muted-foreground text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Medikei" className="h-7 w-auto dark:brightness-110" />
            <span>
              &copy; {new Date().getFullYear()} Medikei Saúde Digital. Todos os direitos reservados.
            </span>
          </div>

          <div className="flex items-center gap-6">
            <Link to="/privacidade" className="hover:text-foreground transition-colors">
              Privacidade
            </Link>
            <Link to="/termos" className="hover:text-foreground transition-colors">
              Termos de Uso
            </Link>
            <Link to="/" className="text-primary font-semibold hover:underline">
              Voltar ao Portal de Pacientes
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
